import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify';
import { ArrowLeft, Search, UserPlus } from 'lucide-react';

import DataTable from '../ui/DataTable';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { PageLoader } from '../ui/Spinner';
import TeamsService from '../../services/TeamsService';
import TeamMemberService from '../../services/TeamMemberService';

function formatLastSeen(dateStr) {
    if (!dateStr) return 'Never';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function getBatteryColor(battery) {
    if (battery == null) return '#999';
    if (battery > 50) return '#039855';
    if (battery > 20) return '#f79009';
    return '#d92d20';
}

function TeamDetails() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [team, setTeam] = useState(null);
    const [allMembers, setAllMembers] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [search, setSearch] = useState('');

    const loadTeam = async () => {
        try {
            const response = await TeamsService.findById(id);
            if (response?.status === 200) {
                setTeam(response.data);
            }
        } catch (error) {
            toast.error("Failed to load team");
            console.log(error);
        }
    };

    const loadAllMembers = async () => {
        try {
            const response = await TeamMemberService.findAll(1, 500);
            if (response?.status === 200) {
                setAllMembers(response.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        loadTeam();
        loadAllMembers();
    }, [id]);

    const handleRemoveMember = async (memberId, memberName) => {
        if (!window.confirm(`Remove "${memberName}" from this team?`)) return;
        try {
            await TeamsService.removeMember(id, memberId);
            toast.success("Member removed");
            loadTeam();
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to remove member");
        }
    };

    const handleAddMember = async (memberId) => {
        try {
            await TeamsService.addMember(id, memberId);
            toast.success("Member added");
            loadTeam();
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to add member");
        }
    };

    const teamMemberIds = new Set((team?.members || []).map(m => m.id));
    const availableMembers = allMembers.filter(m => !teamMemberIds.has(m.id));
    const filteredMembers = search
        ? availableMembers.filter(m =>
            m.fullname?.toLowerCase().includes(search.toLowerCase()) ||
            m.email?.toLowerCase().includes(search.toLowerCase()) ||
            m.role?.role_name?.toLowerCase().includes(search.toLowerCase())
        )
        : availableMembers;

    const roleVariant = (roleName) => {
        switch (roleName) {
            case 'doctors': return 'green';
            case 'nurses': return 'amber';
            case 'admins': return 'blue';
            case 'rsos': return 'red';
            default: return 'neutral';
        }
    };

    const headCells = [
        { id: 'fullname', label: 'Name' },
        {
            id: 'role.role_name',
            label: 'Role',
            render: (row) => <Badge variant={roleVariant(row.role?.role_name)}>{row.role?.role_name || 'N/A'}</Badge>,
        },
        { id: 'email', label: 'Email' },
        { id: 'mobile', label: 'Mobile' },
        {
            id: 'batteryPercent',
            label: 'Battery',
            render: (row) => (
                <span style={{ color: getBatteryColor(row.batteryPercent), fontWeight: 700 }}>
                    {row.batteryPercent === null || row.batteryPercent === undefined ? '—' : `${row.batteryPercent}%`}
                </span>
            ),
        },
        {
            id: 'lastSeenAt',
            label: 'Last Seen',
            render: (row) => formatLastSeen(row.lastSeenAt),
        },
        {
            id: 'location',
            label: 'Location',
            render: (row) => (row.lat && row.lng ? `${Number(row.lat).toFixed(4)}, ${Number(row.lng).toFixed(4)}` : '—'),
        },
    ];

    if (!team) return <PageLoader label="Loading team…" />;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3 flex-wrap">
                <button
                    onClick={() => navigate("/team")}
                    className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-500"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-bold text-neutral-900">{team.name}</h2>
                <Badge variant="blue">{team.members.length} members</Badge>
                <Button
                    icon={UserPlus}
                    className="ml-auto"
                    onClick={() => { setAddOpen(true); setSearch(''); }}
                >
                    Add Members
                </Button>
            </div>

            <DataTable
                title="Team Roster"
                rows={team.members}
                headCells={headCells}
                onDelete={(row) => handleRemoveMember(row.id, row.fullname)}
                emptyLabel='No members in this team. Click "Add Members" to start.'
            />

            <Modal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                title={`Add Members to ${team.name}`}
                size="md"
                footer={<Button variant="ghost" onClick={() => setAddOpen(false)}>Done</Button>}
            >
                <div className="relative mb-3">
                    <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        className="form-input pl-9"
                        placeholder="Search by name, email, or role..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="max-h-96 overflow-y-auto divide-y divide-neutral-100">
                    {filteredMembers.length === 0 && (
                        <p className="text-sm text-neutral-400 text-center py-6">No available members</p>
                    )}
                    {filteredMembers.map((member) => (
                        <button
                            key={member.id}
                            onClick={() => handleAddMember(member.id)}
                            className="w-full flex items-center justify-between gap-3 py-3 px-2 text-left hover:bg-neutral-50 rounded-lg"
                        >
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-neutral-800 truncate">{member.fullname}</p>
                                <p className="text-xs text-neutral-500 truncate">
                                    {member.role?.role_name || 'N/A'} &mdash; {member.email}
                                </p>
                            </div>
                            <UserPlus className="w-4 h-4 text-seha-500 shrink-0" />
                        </button>
                    ))}
                </div>
            </Modal>
        </div>
    );
}

export default TeamDetails
